// LEGO compatible color palette with official LEGO color names
export const LEGO_COLORS = [
  { name: "White", r: 255, g: 255, b: 255 },
  { name: "Dark Stone Gray", r: 27, g: 42, b: 52 },
  { name: "Bright Red", r: 196, g: 40, b: 28 },
  { name: "Dark Green", r: 0, g: 143, b: 0 },
  { name: "Earth Blue", r: 0, g: 84, b: 166 },
  { name: "Bright Yellow", r: 255, g: 243, b: 0 },
  { name: "Flame Orangey Yellow", r: 241, g: 90, b: 34 },
  { name: "Medium Lavender", r: 123, g: 46, b: 122 },
  { name: "Reddish Brown", r: 123, g: 75, b: 49 },
  { name: "Light Stone Gray", r: 199, g: 213, b: 211 },
  { name: "Medium Stone Gray", r: 105, g: 105, b: 105 },
  { name: "Cool Yellow", r: 0, g: 158, b: 158 },
  { name: "Silver", r: 140, g: 140, b: 140 },
  { name: "Orange", r: 255, g: 128, b: 0 },
  { name: "Sand Blue", r: 76, g: 146, b: 193 },
  { name: "Sand Yellow", r: 234, g: 206, b: 147 },
  { name: "Medium Azur", r: 155, g: 89, b: 181 },
  { name: "Bright Green", r: 70, g: 153, b: 71 },
  { name: "Bright Yellowish Green", r: 155, g: 205, b: 55 },
  { name: "Earth Green", r: 39, g: 70, b: 145 },
  { name: "Nougat", r: 238, g: 196, b: 182 },
];

// Palette modes: restricted subsets of the canonical palette for
// grayscale / classic-color mosaics. Each entry carries `canonicalIndex`
// (its position in LEGO_COLORS) so pixelation output can be remapped and
// all downstream consumers (canvas, BOM, instructions) keep resolving
// against the canonical 21-color palette.
export interface PaletteModeColor {
  name: string;
  r: number;
  g: number;
  b: number;
  canonicalIndex: number;
}

export type PaletteMode = "full" | "grayscale" | "classic";

const pickColors = (...indices: number[]): PaletteModeColor[] =>
  indices.map((i) => ({ ...LEGO_COLORS[i], canonicalIndex: i }));

export const PALETTE_MODES: Record<PaletteMode, PaletteModeColor[]> = {
  full: LEGO_COLORS.map((c, i) => ({ ...c, canonicalIndex: i })),
  grayscale: pickColors(0, 1, 9, 10, 12),
  classic: pickColors(0, 1, 2, 3, 4, 5),
};

// Pixelation returns indices into whichever palette was passed in; remap
// them to canonical LEGO_COLORS indices so the rest of the app never needs
// to know which mode produced the mosaic.
export const remapToCanonicalIndices = (
  pixels: number[][],
  palette: ReadonlyArray<PaletteModeColor>,
): number[][] =>
  pixels.map((row) => row.map((i) => palette[i].canonicalIndex));

export const normalizeColorIndex = (value: number) => {
  return Number.isInteger(value) && value >= 0 && value < LEGO_COLORS.length
    ? value
    : 0;
};

export const getLegoColor = (colorIndex: number) => {
  return LEGO_COLORS[normalizeColorIndex(colorIndex)] ?? LEGO_COLORS[0];
};

export const getColorHex = (colorIndex: number) => {
  const color = getLegoColor(colorIndex);
  return `rgb(${color.r}, ${color.g}, ${color.b})`;
};
